import supabase from '../lib/supabaseClient';

const deriveDifficultyLabel = (cards = []) => {
  if (!cards.length) {
    return 'Beginner';
  }

  const averageDifficulty = cards.reduce((sum, card) => sum + (card.difficulty ?? 1), 0) / cards.length;

  if (averageDifficulty < 1.7) {
    return 'Beginner';
  }

  if (averageDifficulty < 2.4) {
    return 'Intermediate';
  }

  return 'Advanced';
};

const formatDeckName = (deck) => deck.title || deck.name || 'Untitled Deck';

const normaliseTags = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
};

export const fetchFlashcardDeckSummaries = async (userId) => {
  const { data: decks, error: decksError } = await supabase
    .from('flashcard_decks')
    .select('id, title, description, tags, color, created_at, updated_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });

  if (decksError) {
    throw decksError;
  }

  if (!decks?.length) {
    return [];
  }

  const deckIds = decks.map((deck) => deck.id);

  const { data: cards, error: cardsError } = await supabase
    .from('flashcards')
    .select('id, deck_id, user_id, difficulty, due_at, ease_factor')
    .in('deck_id', deckIds)
    .eq('user_id', userId);

  if (cardsError) {
    throw cardsError;
  }

  const { data: sessions, error: sessionsError } = await supabase
    .from('study_sessions')
    .select('id, deck_id, started_at, cards_reviewed')
    .in('deck_id', deckIds)
    .eq('user_id', userId);

  if (sessionsError) {
    throw sessionsError;
  }

  const todayISO = new Date().toISOString().slice(0, 10);

  return decks.map((deck) => {
    const deckCards = cards?.filter((card) => card.deck_id === deck.id) ?? [];
    const deckSessions = sessions?.filter((session) => session.deck_id === deck.id) ?? [];

    const studiedToday = deckSessions
      ?.filter((session) => session.started_at?.slice(0, 10) === todayISO)
      ?.reduce((acc, session) => acc + (session.cards_reviewed ?? 0), 0) ?? 0;

    const mastered = deckCards.filter((card) => (card.ease_factor ?? 2.5) >= 2.8 || (card.difficulty ?? 1) >= 3).length;

    const lastSession = deckSessions.reduce((latest, session) => {
      if (!session?.started_at) {
        return latest;
      }

      if (!latest) {
        return session;
      }

      return new Date(session.started_at) > new Date(latest.started_at) ? session : latest;
    }, null);

    return {
      id: deck.id,
      name: formatDeckName(deck),
      description: deck.description || '',
      tags: deck.tags || [],
      color: deck.color,
      cardCount: deckCards.length,
      studiedToday,
      mastered,
      category: deck.tags?.[0] || 'General',
      lastStudiedAt: lastSession?.started_at || null,
      difficultyLabel: deriveDifficultyLabel(deckCards),
    };
  });
};

export const createFlashcardDeck = async ({
  userId,
  name,
  description,
  tags,
  color
}) => {
  if (!userId) {
    throw new Error('User ID is required to create a deck.');
  }

  const payload = {
    user_id: userId,
    title: name?.trim() || 'Untitled Deck',
    description: description?.trim() || null,
    tags: normaliseTags(tags),
    color: color?.trim() || null
  };

  const { data, error } = await supabase
    .from('flashcard_decks')
    .insert(payload)
    .select('id, title, description, tags, color, created_at, updated_at')
    .single();

  if (error) {
    throw error;
  }

  return {
    id: data.id,
    name: formatDeckName(data),
    description: data.description || '',
    tags: data.tags || [],
    color: data.color,
    cardCount: 0,
    studiedToday: 0,
    mastered: 0,
    category: data.tags?.[0] || 'General',
    lastStudiedAt: null,
    difficultyLabel: 'Beginner'
  };
};
